// YERİ: src/app/domains/website/sections/mkt-plan-features.ts
//
// Fiyat tablosundaki paket özellik satırları ("10 team members", "20 GB storage").
//
// SAYI paketten (Package limitleri), CÜMLE CMS'ten. Satırlar bir dönem yalnız
// backend'de İngilizce üretiliyordu: Türkçe, Farsça, Arapça fiyat sayfası da
// "10 team members" yazıyordu ve panelden değiştirmenin yolu yoktu. Artık her
// satırın kalıbı mkt-pricing bloğunda (dil başına) duruyor; boş bırakılan kalıp
// bugünkü İngilizce cümleye düşer — yani panel doldurulmadan site değişmez.
//
// Kalıp biçimi: "{n}" sayıyı koyar. Tekil/çoğul "|" ile ayrılır:
//   "1 team member|{n} team members"             → one | other
//   "{n} участник|{n} участника|{n} участников|{n} участника" → one | few | many | other
// Dilin çoğul kuralını Intl.PluralRules seçer; kalıpta o biçim yoksa sonuncusu.

import { SiteConfig, cfg } from '@/app/domains/site/sections/section.model';
import { WebPlanLimits } from '../core/web.models';

/** Satır → CMS alan anahtarı ve bugünkü (backend ile aynı) İngilizce kalıp. */
export const PLAN_FEATURE_DEFAULTS = {
  featTeam:               '1 team member|{n} team members',
  featTeamUnlimited:      'Unlimited team members',
  featCompanies:          '1 company|{n} companies',
  featCompaniesUnlimited: 'Unlimited companies',
  featBranches:           '1 branch per company|{n} branches per company',
  featBranchesUnlimited:  'Unlimited branches',
  featSites:              '1 website|{n} websites',
  featSitesUnlimited:     'Unlimited websites',
  featStorage:            '{n} GB storage',
  featAiRequests:         '{n} Melbear AI requests a month',
  featAiIncluded:         'Melbear AI included',
  featSocialPosts:        '{n} social posts a month',
  featAiTier:             'AI models up to {tier}',
} as const;

export type PlanFeatureKey = keyof typeof PLAN_FEATURE_DEFAULTS;

const PLURAL_ORDER: Record<number, Intl.LDMLPluralRule[]> = {
  2: ['one', 'other'],
  3: ['one', 'few', 'other'],
  4: ['one', 'few', 'many', 'other'],
};

/** Kalıbı sayıya göre çekimler ve sayıyı dilin biçimiyle yazar. */
export function formatPlural(template: string, n: number, locale: string): string {
  const forms = template.split('|').map((f) => f.trim());
  let chosen = forms[forms.length - 1];

  if (forms.length > 1) {
    let rule: Intl.LDMLPluralRule = 'other';
    try { rule = new Intl.PluralRules(locale || 'en').select(n); } catch { /* bilinmeyen dil */ }
    const order = PLURAL_ORDER[forms.length] ?? PLURAL_ORDER[2];
    const index = order.indexOf(rule);
    chosen = forms[index >= 0 ? index : forms.length - 1];
  }

  let formatted = String(n);
  try { formatted = new Intl.NumberFormat(locale || 'en').format(n); } catch { /* bilinmeyen dil */ }
  return chosen.replace(/\{n\}/g, formatted);
}

/**
 * Backend DescribeLimits ile AYNI sıra ve AYNI kurallar:
 * null = sınırsız, 0/negatif = satır yok, AI limiti yoksa (kurumsal değilse)
 * "dahil" satırı.
 */
export function planFeatureLines(
  limits: WebPlanLimits,
  isEnterprise: boolean,
  config: SiteConfig,
  locale: string,
): string[] {
  const tpl = (key: PlanFeatureKey) => cfg(config, key, PLAN_FEATURE_DEFAULTS[key] as string);
  const lines: string[] = [];

  const limit = (value: number | null | undefined, key: PlanFeatureKey, unlimitedKey: PlanFeatureKey) => {
    if (value === null || value === undefined) { lines.push(tpl(unlimitedKey)); return; }
    if (value <= 0) return;
    lines.push(formatPlural(tpl(key), value, locale));
  };

  limit(limits.teamMembers, 'featTeam', 'featTeamUnlimited');
  limit(limits.companies, 'featCompanies', 'featCompaniesUnlimited');
  limit(limits.branchesPerCompany, 'featBranches', 'featBranchesUnlimited');
  limit(limits.sites, 'featSites', 'featSitesUnlimited');

  if ((limits.storageGb ?? 0) > 0) lines.push(formatPlural(tpl('featStorage'), limits.storageGb!, locale));

  if ((limits.aiRequests ?? 0) > 0) lines.push(formatPlural(tpl('featAiRequests'), limits.aiRequests!, locale));
  else if (!isEnterprise) lines.push(tpl('featAiIncluded'));

  if ((limits.socialPostsPerMonth ?? 0) > 0) {
    lines.push(formatPlural(tpl('featSocialPosts'), limits.socialPostsPerMonth!, locale));
  }

  if (limits.aiTier) lines.push(tpl('featAiTier').replace(/\{tier\}/g, limits.aiTier));

  return lines.filter((line) => line.trim().length > 0);
}
